// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Add, Close } from '@carbon/icons-react';
import { formatContextLabel } from '../../utils/askHeliosContext';
import type { AlarmContextSeed } from '../../types';

interface ContextChipsRowProps {
  /** The alarm this thread is scoped to, if any — shown as a removable chip. */
  threadSeed: AlarmContextSeed | null;
  /**
   * True once `threadSeed` has gone out on a request. It is then part of the
   * provider's thread history for good, so the chip stops offering "x" —
   * removing it client-side would just mislabel the thread as ambient while
   * the model stays alarm-contextualized.
   */
  contextSeedSent: boolean;
  /** The alarm on screen right now, if any. */
  pageAlarmContext: AlarmContextSeed | null;
  onAttach: (_seed: AlarmContextSeed) => void;
  onRemove: () => void;
  /** True while a request is in flight — attach/remove are disabled to avoid
   * racing the in-flight request's own seed-sent bookkeeping. */
  disabled?: boolean;
}

/**
 * Removable/addable context chips shown above the message input, so the
 * operator can see (and control) what alarm the next question will carry.
 *
 * Two independent chips, because the on-screen alarm and the thread's
 * attached alarm can differ — the operator can be scoped to one alarm's
 * thread while browsing a different alarm's page:
 *  - the attached chip reflects `threadSeed`, removable with an "x" only
 *    before it has been sent
 *  - the addable chip (with a "+") appears whenever `pageAlarmContext` isn't
 *    already the attached one, letting the operator opt in
 */
function ContextChipsRow({
  threadSeed,
  contextSeedSent,
  pageAlarmContext,
  onAttach,
  onRemove,
  disabled = false,
}: ContextChipsRowProps) {
  const addable =
    pageAlarmContext && pageAlarmContext.id !== threadSeed?.id ? pageAlarmContext : null;

  if (!threadSeed && !addable) return null;

  return (
    <div className="akh-context-row" aria-label="Chat context">
      {threadSeed && (
        <span className="akh-ctx-chip akh-ctx-chip-attached">
          {formatContextLabel(threadSeed)}
          {!contextSeedSent && (
            <button
              type="button"
              className="akh-ctx-chip-remove"
              onClick={onRemove}
              disabled={disabled}
              aria-label={`Remove ${formatContextLabel(threadSeed)} from context`}
            >
              <Close size={12} />
            </button>
          )}
        </span>
      )}
      {addable && (
        <button
          type="button"
          className="akh-ctx-chip akh-ctx-chip-addable"
          onClick={() => onAttach(addable)}
          disabled={disabled}
          aria-label={`Add ${formatContextLabel(addable)} to context`}
        >
          <Add size={12} />
          {formatContextLabel(addable)}
        </button>
      )}
    </div>
  );
}

export default ContextChipsRow;
